// Same-origin JSON calls to /api. A 401 tells the app that the session ended.
import type {
  CreateHabitRequest,
  DataResponse,
  ErrorCode,
  Habit,
  MeResponse,
  SetCompletionRequest,
  UpdateHabitRequest,
} from '../../shared/api';

export class ApiRequestError extends Error {
  constructor(
    readonly code: ErrorCode | 'network',
    message: string,
    readonly status = 0,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Worth retrying later: no connection, or the server/database is unavailable. */
  get transient(): boolean {
    return this.code === 'network' || this.status === 502 || this.status === 503 || this.status === 504;
  }
}

const unauthorizedListeners = new Set<() => void>();
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiRequestError('network', 'Keine Verbindung zum Server.');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const error = (data as { error?: { code?: ErrorCode; message?: string } } | null)?.error;
    if (res.status === 401) for (const listener of unauthorizedListeners) listener();
    throw new ApiRequestError(
      error?.code ?? (res.status >= 500 ? 'unavailable' : 'bad_request'),
      error?.message ?? 'Das hat nicht geklappt. Bitte versuche es erneut.',
      res.status,
    );
  }
  return data as T;
}

export const api = {
  me: () => request<MeResponse>('GET', '/api/me'),
  data: () => request<DataResponse>('GET', '/api/data'),
  createHabit: (body: CreateHabitRequest) => request<Habit>('POST', '/api/habits', body),
  updateHabit: (id: string, patch: UpdateHabitRequest) => request<Habit>('PATCH', `/api/habits/${id}`, patch),
  deleteHabit: (id: string) => request<void>('DELETE', `/api/habits/${id}`),
  reorder: (ids: string[]) => request<void>('PUT', '/api/habit-order', { ids }),
  setCompletion: (body: SetCompletionRequest) => request<void>('PUT', '/api/completions', body),
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    return error.code === 'network' ? 'Du bist offline. Bitte versuche es mit Verbindung erneut.' : error.message;
  }
  return 'Das hat nicht geklappt. Bitte versuche es erneut.';
}
